import { useInfiniteQuery } from '@tanstack/react-query';

import { InfiniteQueryFunction, useRequest } from '@frontend/api';

import { getContactsQueryKey } from './keys';

export type ContactSearchResult = {
  data: {
    id: number;
    fullName: string;
    firstName: string;
    lastName: string;
    email: string;
  };
};

export type ContactSearchData = {
  results: ContactSearchResult[];
  count: number;
};

export type UseContactSearchParams = {
  query?: string;
  filters?: { accounts?: { idIn?: number[] } };
};

export const getContactSearchQueryKey = (params: UseContactSearchParams) =>
  [...getContactsQueryKey(), 'contactSearch', params] as const;

export type ContactSearchQueryKey = ReturnType<typeof getContactSearchQueryKey>;

const PAGE_SIZE = 10;

export const useContactSearch: InfiniteQueryFunction<
  ContactSearchData,
  [UseContactSearchParams],
  ContactSearchQueryKey
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryKey = getContactSearchQueryKey(params);

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ signal, pageParam = 0 }) =>
      request('/search/contacts', {
        init: {
          method: 'POST',
          signal,
          body: { ...params, paginateFrom: pageParam },
        },
      }),
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.length * PAGE_SIZE;
      return fetchedCount < lastPage.count ? fetchedCount : undefined;
    },
    ...queryOptions,
  });
};
